import type { Extensions } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { FontSize, TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import FontFamily from '@tiptap/extension-font-family';
import Highlight from '@tiptap/extension-highlight';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { PaginationPlus } from 'tiptap-pagination-plus';
import { TableKitPlus } from 'tiptap-table-plus';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';

import { ExportDocx } from './services/export-docx';
import { PaginatedTableRowView } from './components/PaginatedTableRowView';
import DocumentImageExtension from './components/DocumentImageExtension';
import DocumentImagePlaceholderExtension, {
  type DocumentImagePlaceholderUiActions,
} from './components/DocumentImagePlaceholderExtension';
import BlockIdExtension from './components/BlockIdExtension';

/**
 * Toggle between table implementations.
 * - `true`:  Standard @tiptap/extension-table + custom grid-based row nodeView
 *            for true row-by-row pagination. Drops duplicateRow/duplicateColumn.
 * - `false`: tiptap-table-plus (TableKitPlus) — original behavior with
 *            scrollable tbody containment.
 *
 * Flip this flag to switch back. No data migration is needed because both
 * implementations use the same ProseMirror schema and colwidth attributes.
 */
export const USE_STANDARD_TABLE = true;

export type DocumentEditorPaginationConfig = Parameters<typeof PaginationPlus.configure>[0];

export interface DocumentEditorExtensionsOptions {
  getPlaceholderUiActions: () => DocumentImagePlaceholderUiActions | null;
  pagination: DocumentEditorPaginationConfig;
}

/**
 * The document editor's full extension set, extracted so tests can build the
 * exact production schema without mounting the editor page.
 */
export function createDocumentEditorExtensions({
  getPlaceholderUiActions,
  pagination,
}: DocumentEditorExtensionsOptions): Extensions {
  return [
    StarterKit.configure({
      blockquote: false,
      code: false,
      codeBlock: false,
      link: {
        openOnClick: false,
        autolink: true,
      },
    }),
    BlockIdExtension,
    TextAlign.configure({
      types: ['heading', 'paragraph', 'tableCell', 'tableHeader'],
    }),
    TextStyle,
    Color,
    FontFamily.configure({
      types: ['textStyle'],
    }),
    FontSize.configure({
      types: ['textStyle'],
    }),
    Highlight.configure({
      multicolor: true,
    }),
    TaskList,
    TaskItem.configure({
      nested: true,
      HTMLAttributes: {
        'data-type': 'taskItem',
      },
    }),
    ExportDocx,
    DocumentImagePlaceholderExtension.configure({
      getUiActions: getPlaceholderUiActions,
    }),
    DocumentImageExtension.configure({
      inline: false,
      allowBase64: false,
    }),
    ...(USE_STANDARD_TABLE
      ? [
          Table.configure({
            resizable: true,
            cellMinWidth: 96,
            handleWidth: 6,
            lastColumnResizable: true,
            allowTableNodeSelection: true,
          }),
          TableRow.extend({
            addNodeView() {
              return PaginatedTableRowView;
            },
          }),
          TableCell,
          TableHeader,
        ]
      : [
          TableKitPlus.configure({
            table: {
              resizable: true,
              cellMinWidth: 96,
              handleWidth: 6,
              lastColumnResizable: true,
              allowTableNodeSelection: true,
            },
          }),
        ]),
    PaginationPlus.configure(pagination),
  ];
}
